import { cn } from '@pubstack/ui/utils/cn'
import { Alert as MantineAlert, Group, Stack } from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import { buildSparkForPubPlacementId, SPARK_FOR_PUB_MAX_DECLARED_PLACEMENTS, SPARK_FOR_PUB_MIN_DECLARED_PLACEMENTS } from '@pubstack/common/src/bidder-seat'
import { BidParamSpec } from '@pubstack/common/src/bidderCatalog'
import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { CellBase, Matrix, Spreadsheet } from 'react-spreadsheet'
import { Sizes } from '~/assets/style/tokens'
import { Header, RowIndicator, SpreadsheetContainer } from '~/components/_styled/spreadsheet'
import { Title_P1 } from '~/components/_styled/titles'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { SpreadsheetCell } from '~/feature-components/SpreadsheetCell'
import { CreateSeatFormModel } from './CreateSeatStepper'

const TRACKING_PREFIX = 'pbs-create-seat-stepper-placements'

export type DeclaredPlacementRow = { placementId: string; bidParams: Record<string, string> }

// Some bidders (e.g. Adform) have no required bid param: still ask for at least one value, so a bare placement ID never counts as declared.
export const isDeclaredPlacementComplete = (row: DeclaredPlacementRow | undefined, bidParamSpecs: BidParamSpec[]): boolean =>
  !!row?.placementId &&
  bidParamSpecs.filter((spec) => spec.required).every((spec) => !!row.bidParams?.[spec.name]?.trim()) &&
  (bidParamSpecs.length === 0 || bidParamSpecs.some((spec) => !!row.bidParams?.[spec.name]?.trim()))

export const countCompleteDeclaredPlacements = (rows: DeclaredPlacementRow[] | undefined, bidParamSpecs: BidParamSpec[]): number =>
  (rows ?? []).filter((row) => isDeclaredPlacementComplete(row, bidParamSpecs)).length

const rowsToMatrix = (rows: DeclaredPlacementRow[], bidParamSpecs: BidParamSpec[]): Matrix<CellBase> =>
  rows.map((row) => [{ value: row.placementId, readOnly: true }, ...bidParamSpecs.map((spec) => ({ value: row.bidParams[spec.name] ?? '' }))])

// Placement IDs are generated from the row position, never read from the matrix: the first column is read-only.
const matrixToRows = (matrix: Matrix<CellBase>, bidderCode: string, bidParamSpecs: BidParamSpec[]): DeclaredPlacementRow[] =>
  matrix.map((row, rowIndex) => ({
    placementId: buildSparkForPubPlacementId(bidderCode, rowIndex + 1),
    bidParams: Object.fromEntries(bidParamSpecs.map((spec, index) => [spec.name, String(row[index + 1]?.value ?? '')]).filter(([, value]) => String(value).trim() !== '')),
  }))

type Props = {
  bidderDisplayName: string
  bidderCode: string
  bidParamSpecs: BidParamSpec[]
}

export const DeclaredPlacementsSection: FunctionComponent<Props> = ({ bidderDisplayName, bidderCode, bidParamSpecs }) => {
  const { getValues, setValue } = useFormContext<CreateSeatFormModel>()
  // The spreadsheet owns the rows while mounted; the form keeps them across step navigation.
  const [rows, setRows] = useState<DeclaredPlacementRow[]>(() => getValues('declaredPlacements'))

  const completeCount = countCompleteDeclaredPlacements(rows, bidParamSpecs)
  const headers = useMemo(() => ['Placement ID', ...bidParamSpecs.map((spec) => spec.name)], [bidParamSpecs])
  const data = useMemo(() => rowsToMatrix(rows, bidParamSpecs), [rows, bidParamSpecs])

  const handleChange = useCallback(
    (matrix: Matrix<CellBase>) => {
      const next = matrixToRows(matrix, bidderCode, bidParamSpecs)
      setRows(next)
      setValue('declaredPlacements', next)
    },
    [bidderCode, bidParamSpecs, setValue]
  )
  const debouncedHandleChange = useDebouncedCallback(handleChange, 100)

  const handleAddPlacement = () => {
    const next = [...rows, { placementId: buildSparkForPubPlacementId(bidderCode, rows.length + 1), bidParams: {} }]
    setRows(next)
    setValue('declaredPlacements', next)
  }

  return (
    <Stack gap={Sizes[16]}>
      <Group justify="space-between" align="baseline">
        <Title_P1>Placement IDs</Title_P1>
        <span className={`font-roboto text-sm ${completeCount < SPARK_FOR_PUB_MIN_DECLARED_PLACEMENTS ? 'text-alert' : 'text-hurricane'}`}>
          {completeCount}/{SPARK_FOR_PUB_MAX_DECLARED_PLACEMENTS} declared
        </span>
      </Group>
      <div className="font-roboto text-base leading-[1.625rem] text-jet">Add the placement IDs that Spark should use when bidding through {bidderDisplayName}.</div>
      <MantineAlert
        variant="light"
        icon={<Icon name={'info'} width={Sizes[16]} fill={'var(--color-king)'} />}
        title="Minimum placement required"
        styles={{
          root: { backgroundColor: 'var(--color-topaze)', border: 'none' },
          title: { color: 'var(--color-king)' },
          message: { color: 'var(--color-ink)' },
        }}
      >
        You must add at least {SPARK_FOR_PUB_MIN_DECLARED_PLACEMENTS} placement IDs for each bid param.
      </MantineAlert>
      <SpreadsheetContainer>
        <Spreadsheet
          ColumnIndicator={({ column, onSelect, selected, label }) => {
            const isRequired = column > 0 && bidParamSpecs[column - 1]?.required
            return (
              <Header onClick={(event) => onSelect(column, event.shiftKey)} className={cn(['Spreadsheet__header', selected && 'Spreadsheet__header--selected'])}>
                {label} {isRequired && '*'}
              </Header>
            )
          }}
          Cell={(props) => <SpreadsheetCell {...props} />}
          RowIndicator={({ onSelect, row, selected, label }) => (
            <RowIndicator selected={selected} onClick={(event) => onSelect(row, event.shiftKey)} className={cn(['Spreadsheet__header', selected && 'Spreadsheet__header--selected'])}>
              {label ?? row + 1}
            </RowIndicator>
          )}
          css={{
            overflowY: 'auto',
            overflowX: 'auto',
            maxHeight: '440px',
          }}
          data={data}
          columnLabels={headers}
          rowLabels={rows.map((_, index) => `${index + 1}`)}
          onChange={(matrix) => debouncedHandleChange(matrix)}
          // Needs to be set to an empty function to avoid performance issues
          // we don't use the formula parser in this context
          createFormulaParser={() => () => {}}
        />
      </SpreadsheetContainer>
      <div>
        <Button variant={'secondary'} iconName={'add'} disabled={rows.length >= SPARK_FOR_PUB_MAX_DECLARED_PLACEMENTS} onClick={handleAddPlacement} trackingLabel={`${TRACKING_PREFIX}-new`}>
          Add placement
        </Button>
      </div>
    </Stack>
  )
}
